import React, { useState, useEffect, useRef } from 'react';

const PeaceMode = ({ audioContext, isSoundEnabled }) => {
  const [birds, setBirds] = useState([]);
  const [hearts, setHearts] = useState([]);
  const [rainbows, setRainbows] = useState([]);
  const [windParticles, setWindParticles] = useState([]);
  const [isWindActive, setIsWindActive] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [lastRClickTime, setLastRClickTime] = useState(0);
  const [rClickCount, setRClickCount] = useState(0);
  const animFrameRef = useRef(null);
  const handlersRef = useRef({});

  useEffect(() => {
    const t = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(t);
  }, []);

  // ─── AUDIO ─────────────────────────────────────────────────────────────────
  const playBirdSound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Chirp: rapid frequency jump
    const chirpSeq = [880, 1047, 880, 1175, 1047];
    chirpSeq.forEach((freq, i) => {
      const osc = audioContext.createOscillator(); const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      filter.type = 'bandpass'; filter.frequency.value = 2200; filter.Q.value = 2;
      osc.type = 'sine'; osc.frequency.value = freq;
      const t = now + i * 0.08;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.06, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      osc.start(t); osc.stop(t + 0.12);
    });
  };

  const playHeartSound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Warm minor 3rd + 5th chord (love chord)
    [261.63, 329.63, 392, 523.25].forEach((freq, i) => {
      const osc = audioContext.createOscillator(); const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      filter.type = 'lowpass'; filter.frequency.value = 800;
      osc.type = 'sine'; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + i * 0.04);
      gain.gain.linearRampToValueAtTime(0.045, now + i * 0.04 + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.04 + 1.4);
      osc.start(now + i * 0.04); osc.stop(now + i * 0.04 + 1.5);
    });
  };

  const playRainbowSound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Magical ascending glissando
    const notes = [261.63, 293.66, 329.63, 349.23, 392, 440, 493.88, 523.25, 587.33, 659.26, 783.99];
    notes.forEach((freq, i) => {
      const osc = audioContext.createOscillator(); const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      filter.type = 'lowpass'; filter.frequency.value = 2000;
      osc.type = 'sine'; osc.frequency.value = freq;
      const t = now + i * 0.1;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.055, t + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 1.8);
      osc.start(t); osc.stop(t + 1.9);
    });
  };

  const playWindSound = (active) => {
    if (!audioContext || !isSoundEnabled) return;
    if (active) {
      if (audioContext.state === 'suspended') audioContext.resume();
      const now = audioContext.currentTime;
      const bufSize = audioContext.sampleRate * 2;
      const buf = audioContext.createBuffer(1, bufSize, audioContext.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
      const src = audioContext.createBufferSource();
      src.buffer = buf; src.loop = true;
      const filter = audioContext.createBiquadFilter();
      filter.type = 'bandpass'; filter.frequency.value = 600; filter.Q.value = 1;
      const gain = audioContext.createGain();
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.15, now + 0.6);
      src.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      src.start(now);
      // Gentle high shimmer
      const shimmer = audioContext.createOscillator(); const sg = audioContext.createGain();
      const lfo = audioContext.createOscillator(); const lfoG = audioContext.createGain();
      lfo.connect(lfoG); lfoG.connect(shimmer.frequency);
      shimmer.connect(sg); sg.connect(audioContext.destination);
      lfo.type = 'sine'; lfo.frequency.value = 3; lfoG.gain.value = 15;
      shimmer.type = 'sine'; shimmer.frequency.value = 440;
      sg.gain.setValueAtTime(0.001, now); sg.gain.linearRampToValueAtTime(0.03, now + 1.0);
      lfo.start(now); shimmer.start(now);
      window._peaceWindNodes = { src, gain, shimmer, sg, lfo };
    } else {
      if (window._peaceWindNodes) {
        const { gain, sg } = window._peaceWindNodes;
        const now = audioContext.currentTime;
        gain.gain.cancelScheduledValues(now); gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.4);
        sg.gain.cancelScheduledValues(now); sg.gain.setValueAtTime(sg.gain.value, now);
        sg.gain.linearRampToValueAtTime(0, now + 0.4);
        setTimeout(() => {
          try { window._peaceWindNodes.src.stop(); window._peaceWindNodes.shimmer.stop(); window._peaceWindNodes.lfo.stop(); } catch(e){}
          window._peaceWindNodes = null;
        }, 500);
      }
    }
  };

  // ─── ANIMATION LOOP ────────────────────────────────────────────────────────
  useEffect(() => {
    const tick = () => {
      setBirds(prev => prev.map(b => {
        const age = Date.now() - b.born;
        if (age > 10000) return null;
        const wx = b.dir * (b.speed + (isWindActive ? b.dir * 1.5 : 0));
        const wy = Math.sin(age / 400 + b.phase) * 0.5;
        const wingPhase = age / 120;
        let nx = b.x + wx;
        let ny = b.y + wy;
        return { ...b, x: nx, y: ny, wingPhase, opacity: Math.max(0, 1 - (age - 8000) / 2000) };
      }).filter(b => b && b.x > -100 && b.x < window.innerWidth + 100));

      setHearts(prev => prev.map(h => {
        const age = Date.now() - h.born;
        const progress = age / h.duration;
        return {
          ...h,
          y: h.y - h.speed * (isWindActive ? 2 : 1),
          x: h.x + Math.sin(age / 500 + h.phase) * (isWindActive ? 2.5 : 0.8) + (isWindActive ? 1.5 : 0),
          opacity: Math.max(0, 1 - progress),
          scale: 1 + progress * 0.5
        };
      }).filter(h => h.opacity > 0));

      setWindParticles(prev => {
        if (!isWindActive) return prev.filter(p => p.opacity > 0).map(p => ({ ...p, opacity: p.opacity - 0.02 }));
        return [
          ...prev.filter(p => p.opacity > 0).map(p => ({
            ...p, x: p.x + p.vx, y: p.y + Math.sin(Date.now() / 500 + p.phase) * 0.5,
            opacity: p.x > window.innerWidth ? 0 : p.opacity
          })),
          // Spawn new particles
          ...(Math.random() > 0.7 ? [{
            id: Date.now() + Math.random(),
            x: -10, y: 50 + Math.random() * (window.innerHeight - 100),
            vx: 2 + Math.random() * 2, phase: Math.random() * Math.PI * 2,
            opacity: 0.3 + Math.random() * 0.5, size: 2 + Math.random() * 4
          }] : [])
        ];
      });

      setRainbows(prev => prev.map(r => {
        const age = Date.now() - r.born;
        return { ...r, opacity: Math.max(0, 1 - age / 4500) };
      }).filter(r => r.opacity > 0));

      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isWindActive]);

  // ─── INTERACTION ──────────────────────────────────────────────────────────
  handlersRef.current = {
    handleMouseDown: (e) => {
      if (e.button === 0) {
        // Spawn hearts
        const count = 4 + Math.floor(Math.random() * 4);
        setHearts(prev => [...prev, ...Array.from({ length: count }).map((_, i) => ({
          id: Date.now() + i,
          x: e.clientX + (Math.random() - 0.5) * 40,
          y: e.clientY,
          size: 12 + Math.random() * 14,
          hue: 330 + (Math.random() - 0.5) * 40,
          speed: 0.7 + Math.random() * 0.8,
          phase: Math.random() * Math.PI * 2,
          opacity: 1, scale: 1,
          duration: 3500 + Math.random() * 2000,
          born: Date.now()
        }))]);
        setShowHint(false);
        playHeartSound();
      }
      if (e.button === 2) {
        const now = Date.now();
        const tSince = now - lastRClickTime;
        if (tSince < 350 && rClickCount === 1) {
          // Double right-click: rainbow
          const id = Date.now();
          setRainbows(prev => [...prev, { id, x: e.clientX, y: e.clientY, born: Date.now(), opacity: 1 }]);
          playRainbowSound();
          setRClickCount(0);
        } else {
          setRClickCount(1);
          setTimeout(() => setRClickCount(0), 350);
          // Single right-click: bird
          const dir = Math.random() > 0.5 ? 1 : -1;
          setBirds(prev => [...prev, ...Array.from({ length: 3 + Math.floor(Math.random() * 3) }).map((_, i) => ({
            id: Date.now() + i,
            x: dir > 0 ? e.clientX - 20 : e.clientX + 20,
            y: e.clientY + (Math.random() - 0.5) * 30,
            dir, speed: 1.2 + Math.random() * 0.8,
            phase: Math.random() * Math.PI * 2,
            size: 10 + Math.random() * 8,
            hue: Math.random() * 360,
            born: Date.now(), wingPhase: 0, opacity: 1
          }))]);
          playBirdSound();
        }
        setLastRClickTime(now);
        setShowHint(false);
      }
      if (e.button === 1) {
        e.preventDefault();
        setIsWindActive(true);
        playWindSound(true);
      }
    },
    handleMouseUp: (e) => {
      if (e.button === 1) {
        setIsWindActive(false);
        playWindSound(false);
      }
    }
  };

  useEffect(() => {
    const down = (e) => handlersRef.current.handleMouseDown(e);
    const up = (e) => handlersRef.current.handleMouseUp(e);
    const ctx = (e) => e.preventDefault();
    window.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    window.addEventListener('contextmenu', ctx);
    return () => {
      window.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('contextmenu', ctx);
    };
  }, []);

  const heartPath = (cx, cy, size) => {
    const s = size;
    return `M${cx},${cy + s*0.3} C${cx - s*1.2},${cy - s*0.8} ${cx - s*2},${cy + s*0.8} ${cx},${cy + s*1.8} C${cx + s*2},${cy + s*0.8} ${cx + s*1.2},${cy - s*0.8} ${cx},${cy + s*0.3}Z`;
  };

  const birdPath = (size, t) => {
    const ws = size * 2.1;
    const flap = (Math.sin(t) + 1.0) * 0.5; // 0..1
    const tip = (0.18 + flap * 0.68) * size;  // wing tips: always upward, oscillate
    const ctrl = tip * 0.08;                   // slight downward bow at body center
    return `M${-ws},${-tip} Q${-ws*0.42},${ctrl} 0,0 Q${ws*0.42},${ctrl} ${ws},${-tip}`;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', zIndex: 99990, pointerEvents: 'none' }}>
      <style>{`
        @keyframes hintFadeIn { 0%{opacity:0;transform:translateY(20px)} 100%{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* Wind ambient overlay */}
      {isWindActive && (
        <div style={{
          position: 'fixed', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(90deg, transparent 0%, rgba(200,240,255,0.05) 50%, transparent 100%)',
          animation: 'none'
        }} />
      )}

      <svg style={{ position: 'absolute', width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
        <defs>
          <filter id="peaceGlow">
            <feGaussianBlur stdDeviation="2.5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* Wind particles */}
        {windParticles.map(p => (
          <circle key={p.id} cx={p.x} cy={p.y} r={p.size}
            fill={`rgba(200,230,255,${p.opacity})`} />
        ))}

        {/* Rainbows */}
        {rainbows.map(r => {
          const rcx = window.innerWidth / 2;
          const rcy = r.y;
          const R = Math.min(window.innerWidth * 0.43, 600);
          const bands = [
            { c: 'rgba(255,35,35,0.78)',   r: R,     w: 14 },
            { c: 'rgba(255,125,0,0.78)',   r: R-15,  w: 13 },
            { c: 'rgba(255,225,0,0.78)',   r: R-30,  w: 13 },
            { c: 'rgba(30,195,55,0.78)',   r: R-45,  w: 13 },
            { c: 'rgba(30,120,255,0.78)',  r: R-60,  w: 13 },
            { c: 'rgba(145,0,225,0.72)',   r: R-75,  w: 12 },
          ];
          return (
            <g key={r.id} opacity={r.opacity}>
              <path d={`M${rcx-R-15},${rcy} A${R+15},${R+15} 0 0 1 ${rcx+R+15},${rcy}`}
                fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="110"
                style={{ filter: 'blur(24px)' }} />
              {bands.map((b, i) => (
                <path key={i}
                  d={`M${rcx - b.r},${rcy} A${b.r},${b.r} 0 0 1 ${rcx + b.r},${rcy}`}
                  fill="none" stroke={b.c} strokeWidth={b.w}
                  style={{ filter: 'blur(5px)' }} />
              ))}
            </g>
          );
        })}

        {/* Hearts */}
        {hearts.map(h => (
          <g key={h.id} transform={`translate(${h.x},${h.y}) scale(${h.scale})`} opacity={h.opacity}>
            <path d={heartPath(0, 0, h.size * 0.5)}
              fill={`hsla(${h.hue}, 85%, 65%, 0.9)`}
              filter="url(#peaceGlow)" />
          </g>
        ))}

        {/* Birds */}
        {birds.map(b => (
          <g key={b.id} transform={`translate(${b.x},${b.y})`} opacity={b.opacity}>
            <path d={birdPath(b.size, b.wingPhase)}
              fill="none"
              stroke={`hsla(${b.hue}, 78%, 72%, 0.95)`}
              strokeWidth={Math.max(1.4, b.size * 0.11)}
              strokeLinecap="round" strokeLinejoin="round" />
          </g>
        ))}
      </svg>

      {showHint && (
        <div style={{
          position: 'fixed', bottom: '160px', left: '50%', transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, rgba(80,30,60,0.92), rgba(140,60,100,0.92))',
          backdropFilter: 'blur(16px)', borderRadius: '16px', padding: '14px 22px',
          border: '1px solid rgba(255,160,200,0.3)',
          color: 'rgba(255,210,230,0.95)', fontSize: '13px',
          animation: 'hintFadeIn 0.4s ease', pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 99999
        }}>
          🕊️ ЛКМ — сердечки · ПКМ — птички · ПКМ×2 — радуга · Wheel — ветерок
        </div>
      )}
    </div>
  );
};

export default PeaceMode;
